import React from 'react';
import { AlertCircle, AlertTriangle } from 'lucide-react';
import type { ValidationIssue } from '../../utils/surveyValidation';

interface QuestionIssueBadgeProps {
  issues: ValidationIssue[];
  /** Jump to / focus this question (click-to-fix). */
  onClick?: () => void;
}

/**
 * Inline per-question marker for the editor. Renders a red X badge when a question has a blocking
 * structural error, or an amber badge for warning-only issues, so the author can see exactly which
 * questions still need fixing before the survey can be activated. Hidden when the question is clean.
 */
const QuestionIssueBadge: React.FC<QuestionIssueBadgeProps> = ({ issues, onClick }) => {
  if (!issues || issues.length === 0) return null;

  const hasError = issues.some((i) => i.severity === 'error');
  const Icon = hasError ? AlertCircle : AlertTriangle;
  const label = issues.length === 1 ? '1 issue' : `${issues.length} issues`;
  const title = issues.map((i) => i.message).join('\n');

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid="question-issue-badge"
      data-severity={hasError ? 'error' : 'warning'}
      className={`mr-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
        hasError
          ? 'bg-red-100 text-red-800 border-red-300 hover:bg-red-200'
          : 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200'
      }`}
    >
      <Icon className="w-3 h-3" />
      {label}
    </button>
  );
};

export default QuestionIssueBadge;
